import React from 'react'
import { Flex, Box, Text } from '@clear/design-system'
import { ColorType } from '@clear/design-system/lib/theme/colors'
import Icon from '@components/shared/Icon'

type Step = {
  name: string
}
export interface StepperProps {
  current: number
  steps: Array<Step>
}

const blue = 'dhsBlue' as ColorType
const grey = 'stepperGrey' as ColorType

const StepperCircle = ({
  name,
  color,
  index,
  current,
  total,
}: {
  icon?: JSX.Element
  color: ColorType
  name: string
  index: number
  current: number
  total: number
  isCurrent: boolean
}) => {
  const circleSize = () => {
    if (current === 0) {
      return index === 0 ? '24px' : '0px'
    }
    if (index < current) {
      return '24px'
    }

    if (index === current) {
      return '38px'
    }
    return '0px'
  }
  return (
    <Flex alignItems="center" flexDirection="column" width="0px" flexShrink={0}>
      <Box
        height="38px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {index === total - 1 && current === total ? (
          <Icon
            name="stepper-check"
            alt="check"
            size={38}
            className="xs:mb-4 mb-3"
          />
        ) : (
          <Flex
            height={circleSize()}
            mb={[4, 3]}
            width={circleSize()}
            zIndex={1}
            backgroundColor={color}
            alignItems="center"
            justifyContent="center"
            data-testid={`stepper-circle-${index}`}
            borderRadius="100%"
          />
        )}
      </Box>

      <Box display={['none', 'flex']} mt="4px" height="20px">
        <Text
          color="neutralsTextBlack"
          fontSize={2}
          data-testid={`stepper-text-${index}`}
          sx={{
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Text>
      </Box>
    </Flex>
  )
}

const Spacer = ({
  index,
  current,
  isLast = false,
}: {
  color?: ColorType
  index: number
  current: number
  isLast: boolean
}) => (
  <Flex
    width="100%"
    height={['0.25rem', '0.5rem']}
    mt={[2, 2]}
    backgroundColor={index < current ? blue : grey}
    borderBottomRightRadius={isLast ? '8px' : '0px'}
    borderTopRightRadius={isLast ? '8px' : '0px'}
  />
)

export const Stepper = ({ current, steps }: StepperProps) => {
  return (
    <Box width="100%" background="#E8EAEC" py={2}>
      <Flex
        maxWidth="80%"
        width={['full', 'tablet', 'content']}
        marginX="auto"
        my={5}
      >
        {steps.map(({ name }: Step, index) => {
          const color: ColorType = index <= current ? blue : grey

          return (
            <React.Fragment key={`stepper-${index}`}>
              <StepperCircle
                color={color}
                name={name}
                current={current}
                index={index}
                total={steps.length}
                isCurrent={current === index}
              />
              {index < steps.length - 1 ? (
                <Spacer
                  color={color}
                  index={index}
                  current={current}
                  isLast={index === steps.length - 2}
                />
              ) : null}
            </React.Fragment>
          )
        })}
      </Flex>
    </Box>
  )
}
Stepper.displayName = 'Stepper'
export default Stepper
